import { useEffect } from "react";
import { useSnapshot } from "valtio";
import {
  organizationSftpStore,
  resetOrganizationSftpStore,
} from "../sftpStore";
import { SftpService } from "@services/ingest/sftpService";
import { CreateSftpKeyPayload } from "@services/ingest/commands/cmdCreateSftpKey";
import showAlert from "src/shared/utils/generic.tsx";

const service = new SftpService();

const useOrganizationSftp = (organizationId: number) => {
  const snap = useSnapshot(organizationSftpStore);

  useEffect(() => {
    if (!organizationId) return;
    if (organizationId !== organizationSftpStore.organizationId) {
      resetOrganizationSftpStore();
      organizationSftpStore.organizationId = organizationId;
    }
    loadSettings(organizationId);
  }, [organizationId]);

  async function loadSettings(orgId: number) {
    organizationSftpStore.isLoading = true;
    organizationSftpStore.error = null;
    try {
      organizationSftpStore.settings = await service.getSettings(orgId);
    } catch {
      organizationSftpStore.error = "Failed to load SFTP settings.";
    } finally {
      organizationSftpStore.isLoading = false;
    }
  }

  async function uploadKey(payload: CreateSftpKeyPayload) {
    organizationSftpStore.isSaving = true;
    try {
      await service.createKey(organizationId, payload);
      showAlert("Public key uploaded successfully", "success", 3000);
      await loadSettings(organizationId);
    } catch (err) {
      // Surfaces the server's message, e.g. 400 (invalid/private key) or 409 (already uploaded).
      showAlert(err instanceof Error ? err.message : "Failed to upload public key", "error", 4000);
      throw err;
    } finally {
      organizationSftpStore.isSaving = false;
    }
  }

  async function deleteKey(keyId: number) {
    organizationSftpStore.isSaving = true;
    try {
      await service.deleteKey(organizationId, keyId);
      showAlert("Key revoked successfully", "success", 3000);
      await loadSettings(organizationId);
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Failed to revoke key", "error", 4000);
      throw err;
    } finally {
      organizationSftpStore.isSaving = false;
    }
  }

  async function downloadInstructions() {
    try {
      const blob = await service.getInstructions();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "motimatic-sftp-setup-instructions.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Failed to download the SFTP instructions", "error", 4000);
    }
  }

  return {
    settings: snap.settings,
    isLoading: snap.isLoading,
    isSaving: snap.isSaving,
    error: snap.error,
    uploadKey,
    deleteKey,
    downloadInstructions,
  };
};

export default useOrganizationSftp;
